import {
  Autocomplete,
  Box,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { FormikProps } from "formik";
import { FC, useEffect, useState } from "react";
import { MapPin } from "react-feather";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { useConfig } from "../utils/useConfig";
import { OnboardingFormValuesType } from "./onboarding/OnboardingForm";

export const AddressForm: FC<{
  formik: FormikProps<OnboardingFormValuesType>;
}> = ({ formik }) => {
  const theme = useTheme();
  const { config } = useConfig();
  const { placesService, placePredictions, getPlacePredictions } =
    usePlacesService({
      apiKey: config?.GOOGLE_PLACES_API_KEY,
      options: {
        types: ["address"],
        componentRestrictions: {
          country: ["nz", "au"]
        }
      }
    });

  const [selectedPlaceId, setSelectedPlaceId] = useState();

  const handlePlaceSelected = async (place: any) => {
    const address1 = `${
      place.address_components.find((c: any) =>
        c.types.includes("street_number")
      )?.long_name || ""
    } ${
      place.address_components.find((c: any) => c.types.includes("route"))
        ?.long_name || ""
    }`.trim();

    const address2 =
      place.address_components.find((c: any) => c.types.includes("sublocality"))
        ?.long_name || "";

    const city =
      place.address_components.find((c: any) => c.types.includes("locality"))
        ?.long_name || "";

    const stateOrProvince =
      place.address_components.find((c: any) =>
        c.types.includes("administrative_area_level_1")
      )?.long_name || "";

    const postalCode =
      place.address_components.find((c: any) => c.types.includes("postal_code"))
        ?.long_name || "";

    const country =
      place.address_components.find((c: any) => c.types.includes("country"))
        ?.short_name || "";

    formik.setFieldValue("contact.address.address1", address1);
    formik.setFieldValue("contact.address.address2", address2);
    formik.setFieldValue("contact.address.city", city);
    formik.setFieldValue("contact.address.stateOrProvince", stateOrProvince);
    formik.setFieldValue("contact.address.postalCode", postalCode);
    formik.setFieldValue("contact.address.country", country);
  };

  // When selectedPlaceId updates, get the place details and set values
  useEffect(() => {
    if (!selectedPlaceId) return;
    placesService?.getDetails(
      {
        placeId: selectedPlaceId
      },
      (place: any) => handlePlaceSelected(place)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlaceId]);

  // When input changes, get place predictions
  useEffect(() => {
    getPlacePredictions({ input: formik.values.contact.address.address1 });
  }, [formik.values.contact.address.address1, getPlacePredictions]);

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <Grid item xs={12} marginTop={3}>
        <Autocomplete
          freeSolo
          disableClearable
          options={placePredictions.map((option: any) => option.description)}
          value={formik.values.contact.address.address1}
          onChange={(e, value) => {
            e.preventDefault();
            setSelectedPlaceId(
              placePredictions.find((p: any) => p.description === value)
                .place_id
            );
          }}
          renderOption={(props, option) => {
            return (
              <li {...props}>
                <Grid container alignItems="center">
                  <Grid item sx={{ display: "flex", width: 35 }}>
                    <MapPin width={15} color={theme.palette.primary.main} />
                  </Grid>
                  <Grid
                    item
                    sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
                  >
                    <Typography>{option}</Typography>
                  </Grid>
                </Grid>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Address 1"
              name="contact.address.address1"
              value={formik.values.contact.address.address1}
              onChange={formik.handleChange}
              error={
                formik.touched.contact?.address?.address1 &&
                Boolean(formik.errors.contact?.address?.address1)
              }
              helperText={
                formik.touched.contact?.address?.address1 &&
                formik.errors.contact?.address?.address1?.replace(
                  "contact.address.address1",
                  "Address 1"
                )
              }
              placeholder="Start typing to find your address..."
              required
              fullWidth
              size="small"
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          value={formik.values.contact.address.address2}
          onChange={formik.handleChange}
          name="contact.address.address2"
          error={
            formik.touched.contact?.address?.address2 &&
            Boolean(formik.errors.contact?.address?.address2)
          }
          helperText={
            formik.touched.contact?.address?.address2 &&
            formik.errors.contact?.address?.address2?.replace(
              "contact.address.address2",
              "Address 2"
            )
          }
          label="Address 2"
          fullWidth
          size="small"
        />
      </Grid>

      {formik.values.contact.address.country &&
        formik.values.contact.address.country !== "NZ" && ( // Only show state/province if not in NZ
          <Grid item xs={12}>
            <TextField
              value={formik.values.contact.address.stateOrProvince}
              onChange={formik.handleChange}
              name="contact.address.stateOrProvince"
              error={
                formik.touched.contact?.address?.stateOrProvince &&
                Boolean(formik.errors.contact?.address?.stateOrProvince)
              }
              helperText={
                formik.touched.contact?.address?.stateOrProvince &&
                formik.errors.contact?.address?.stateOrProvince?.replace(
                  "contact.address.stateOrProvince",
                  "State or Province"
                )
              }
              label="State or Province"
              required
              fullWidth
              size="small"
            />
          </Grid>
        )}

      <Grid item xs={12}>
        <TextField
          value={formik.values.contact.address.city}
          onChange={formik.handleChange}
          name="contact.address.city"
          error={
            formik.touched.contact?.address?.city &&
            Boolean(formik.errors.contact?.address?.city)
          }
          helperText={
            formik.touched.contact?.address?.city &&
            formik.errors.contact?.address?.city?.replace(
              "contact.address.city",
              "City"
            )
          }
          label="City"
          required
          fullWidth
          size="small"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          value={formik.values.contact.address.postalCode}
          onChange={formik.handleChange}
          name="contact.address.postalCode"
          error={
            formik.touched.contact?.address?.postalCode &&
            Boolean(formik.errors.contact?.address?.postalCode)
          }
          helperText={
            formik.touched.contact?.address?.postalCode &&
            formik.errors.contact?.address?.postalCode?.replace(
              "contact.address.postalCode",
              "Postal Code"
            )
          }
          label="Postal Code"
          required
          fullWidth
          size="small"
        />
      </Grid>
      <Grid item xs={6}>
        <Select
          value={formik.values.contact.address.country}
          onChange={formik.handleChange}
          name="contact.address.country"
          size="small"
          required
          fullWidth
          error={
            formik.touched.contact?.address?.country &&
            Boolean(formik.errors.contact?.address?.country)
          }
        >
          <MenuItem value="NZ">NZ</MenuItem>
          <MenuItem value="AU">AU</MenuItem>
        </Select>
      </Grid>
    </Box>
  );
};
